import React, { useMemo, useState } from "react";
import Card from "components/card";
import BarChart from "components/charts/BarChart";
import { MdBarChart, MdOutlineCalendarToday } from "react-icons/md";
import { barChartOptionsWeeklyRevenue } from "variables/charts";
import { useServicesWeeklyCounts } from "utils/hooks/useServicesWeeklyCounts";

const WeeklyRevenue = () => {
  // Set default to today in YYYY-MM-DD
  const today = new Date().toISOString().slice(0, 10);
  const [weekOf, setWeekOf] = useState(today);
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const { apex, loading, error } = useServicesWeeklyCounts({
    weekOf,
    includeCancelled,
  });

  const options = useMemo(() => {
    return {
      ...barChartOptionsWeeklyRevenue,
      xaxis: {
        ...barChartOptionsWeeklyRevenue.xaxis,
        categories:
          apex.categories?.length
            ? apex.categories
            : barChartOptionsWeeklyRevenue.xaxis.categories,
      },
      legend: { ...(barChartOptionsWeeklyRevenue.legend || {}), show: true },
    };
  }, [apex]);

  const series = apex?.series ?? [];

  const hasData =
    Array.isArray(series) && series.some((s) => s?.data?.some((v) => v > 0));

  return (
    <Card extra="!p-3 sm:!p-4 text-center min-w-0">

      {/* HEADER */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lg:flex-nowrap">
        <div className="flex items-center gap-2 flex-wrap">

          <button className="linear flex items-center justify-center gap-2 rounded-lg bg-lightPrimary p-1.5 sm:p-2 text-gray-600 transition duration-200 hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:hover:opacity-90 dark:active:opacity-80">
            <MdOutlineCalendarToday className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="text-xs sm:text-sm font-medium text-gray-600">
              Weekly
            </span>
          </button>

          <div className="flex items-center gap-2">
            <input
              type="date"
              className="border rounded-lg px-2 sm:px-3 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:border-navy-600 dark:text-white focus:ring-2 focus:ring-brand-500"
              value={weekOf}
              onChange={(e) => setWeekOf(e.target.value || today)}
              disabled={loading}
            />

            <label className="flex items-center gap-1 text-xs sm:text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-brand-500"
                checked={includeCancelled}
                onChange={(e) => setIncludeCancelled(e.target.checked)}
                disabled={loading}
              />
              Cancelled
            </label>
          </div>
        </div>

        <button className="!linear z-[1] flex shrink-0 items-center justify-center rounded-lg bg-lightPrimary p-1.5 sm:p-2 text-brand-500 transition duration-200 hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10">
          <MdBarChart className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      </div>

      {/* BODY */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lg:flex-nowrap">

        {/* LEFT INFO */}
        <div className="flex flex-col flex-shrink-0 text-left">
          <p className="mt-2 sm:mt-3 text-lg sm:text-2xl md:text-3xl font-bold text-navy-700 dark:text-white">
            {loading ? "..." : apex?.meta?.total ?? "0"}
          </p>

          <p className="mt-0.5 text-xs sm:text-sm text-gray-600">
            Weekly Bookings
          </p>

          {!loading && apex?.meta?.week && (
            <p className="text-xs text-gray-500">
              {apex.meta.week.start} → {apex.meta.week.end}
            </p>
          )}
        </div>

        {/* CHART AREA */}
        <div className="w-full flex-1 max-w-full h-[200px] sm:h-[240px] md:h-[280px]">
          {error ? (
            <div className="flex items-center justify-center h-full text-sm text-red-500">
              {error}
            </div>
          ) : loading ? (
            <div className="w-full h-full animate-pulse bg-gray-100 dark:bg-navy-700 rounded-xl" />
          ) : hasData ? (
            <BarChart chartData={series} chartOptions={options} />
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-gray-500 dark:text-gray-300">
              No data for this week.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default WeeklyRevenue;